import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  Tooltip,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomSelectFile from "../../../comman/CustomSelectFile";
import { apiRequest } from "../../../utils/config/apiRequest";
import { apiUrl } from "../../../utils/endPoints/apiUrl";
import { _toastVariants, FILE_UPLOAD_DIR } from "../../../utils/Constant";
import { showMessage } from "../../../utils/toast/globalToast";
import { LoadingButton } from "@mui/lab";
import AsyncAutocomplete from "../../comman/AsyncAutoComplete";

const ServiceSchema = Yup.object({
  name: Yup.string()
    .required('Service name is required'),
  serviceTimeInMin: Yup.number()
    .typeError('Service time must be a number')
    .positive('Must be greater than 0')
    .required('Service time is required'),

  servicePrice: Yup.number()
    .typeError('Service price must be a number')
    .positive('Must be greater than 0')
    .required('Service price is required'),

  discountedPrice: Yup.number()
    .typeError('Discounted price must be a number')
    .positive('Must be greater than 0')
    .max(
      Yup.ref('servicePrice'),
      'Discounted price cannot be greater than service price'
    )
    .required('Discounted price is required'),
});


export default function AddEditCatgories({
  defaultData = {},
  serviceCategoryId = "",
  onSave = () => { },
  onCreate = () => { },
  shopId,
  shopServiceId,
  shopKeeperId,
}) {

  const [load, setLoad] = useState(false);
  const [selectedAddService, setSelectedAddService] = useState(defaultData?.additionalService ? defaultData?.additionalService?.map(item => ({ ...item, _id: item?.serviceId })) : []);

  const formik = useFormik({
    initialValues: {
      _id: defaultData?._id,
      name: defaultData?.name || '',
      serviceCategoryId: serviceCategoryId || null,
      remarks: defaultData?.remarks || [],
      thumbnailURL: defaultData?.thumbnailURL || "",
      onsite: defaultData?.onsite || false,
      offsite: defaultData?.offsite || false,
      serviceTimeInMin: defaultData?.serviceTimeInMin || 0,
      servicePrice: defaultData?.servicePrice || 0,
      discountedPrice: defaultData?.discountedPrice || 0,
      aboutService: defaultData?.aboutService || [],
      serviceProcessSteps: defaultData?.serviceProcessSteps || [],
      onsiteVisitPrice: defaultData?.onsiteVisitPrice || 0,
      sizeStandard: defaultData?.sizeStandard ? defaultData?.sizeStandard?.map(item => ({ ...item, _id: item?.countryId })) : [],
      urgentCharge: defaultData?.urgentCharge || ''
    },
    validationSchema: ServiceSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values) => handleSave(values)

  });

  const handleSave = async (values) => {
    const payload = {
      ...values,
      remarks: values.remarks.filter((r) => r.trim() !== ""),
      aboutService: values.aboutService.filter((a) => a.trim() !== ""),
      serviceProcessSteps: values.serviceProcessSteps.filter((s) => s.trim() !== ""),
      thumbnailURL: values.thumbnailURL
    };

    if (defaultData?.flow === 'ADD') {
      delete payload.serviceCategoryId;
      payload.shopKeeperId = shopKeeperId;
      payload.shopServiceId = shopServiceId;

      if (typeof values.thumbnailURL !== 'string' && values?.thumbnailURL) {
        const formData = new FormData()
        formData.append('file', values.thumbnailURL);

        let { data, status } = await apiRequest({
          method: "POST",
          endUrl: apiUrl.uploadFile(FILE_UPLOAD_DIR.SHOP),
          body: formData,
          headerType: 'form'
        });

        if (status) {
          payload.thumbnailURL = data?.shortUrl
        }
      }

      setLoad(true)
      let { status } = await apiRequest({
        method: "POST",
        endUrl: apiUrl.branch.addNewServiceCategory(shopId, shopServiceId),
        body: payload,
        showMsg: true
      });
      setLoad(false)
      status && onCreate();
    } else {
      onSave({ ...payload, additionalService: selectedAddService });
    }
  }

  const fetchOptions = async (url, text) => {
    const { data } = await apiRequest({
      endUrl: url,
      query: { searchQ: text }
    });

    return data?.rows || []
  }

  const handlePrice = (e, index) => {
    let tmp = [...selectedAddService]
    tmp[index].price = e.target.value;
    setSelectedAddService(tmp)
  }


  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={2}>
        <Grid item md={12}>
          {/* Thumbnail */}
          <CustomSelectFile
            id="selectServiceCategory"
            title="Thumbnail (jpeg, png, jpg)"
            files={formik.values.thumbnailURL}
            onChange={async (file) => {
              if (!file) return;
              formik.setFieldValue("thumbnailURL", file);
            }}
          />
        </Grid>
        <Grid item md={12}>
          {/* Onsite & Offsite */}
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.onsite}
                  onChange={(e) =>
                    formik.setFieldValue("onsite", e.target.checked)
                  }
                />
              }
              label="Onsite"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.offsite}
                  onChange={(e) =>
                    formik.setFieldValue("offsite", e.target.checked)
                  }
                />
              }
              label="Offsite"
            />
          </Stack>
        </Grid>
        <Grid item md={6}>
          <TextField
            required
            fullWidth
            size="small"
            label="Service Name"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
        </Grid>
        <Grid item md={6}>
          {/* Service Time */}
          <TextField
            required
            fullWidth
            size="small"
            label="Service Time (minutes)"
            name="serviceTimeInMin"
            type="number"
            value={formik.values.serviceTimeInMin}
            onChange={formik.handleChange}
            error={formik.touched.serviceTimeInMin && Boolean(formik.errors.serviceTimeInMin)}
            helperText={formik.touched.serviceTimeInMin && formik.errors.serviceTimeInMin}
          />
        </Grid>
        <Grid item md={6}>
          {/* Service Price */}
          <TextField
            required
            fullWidth
            size="small"
            label="Service Price"
            name="servicePrice"
            type="number"
            value={formik.values.servicePrice}
            onChange={(e) => {
              if (e.target.value < formik.values.discountedPrice) {
                formik.setFieldValue('discountedPrice', 0)
              }
              formik.handleChange(e)
            }}
            error={formik.touched.servicePrice && Boolean(formik.errors.servicePrice)}
            helperText={formik.touched.servicePrice && formik.errors.servicePrice}
          />

        </Grid>
        <Grid item md={6}>
          {/* Discounted Price */}

          <Tooltip title="Final price after applying discount. Must be less than or equal to service price." arrow>
            <TextField
              required
              fullWidth
              size="small"
              label="Price After Discount"
              name="discountedPrice"
              type="number"
              value={formik.values.discountedPrice}
              onChange={(e) => {
                if (e.target.value <= formik.values.servicePrice) {
                  formik.handleChange(e)
                }
              }}
              error={formik.touched.discountedPrice && Boolean(formik.errors.discountedPrice)}
              helperText={formik.touched.discountedPrice && formik.errors.discountedPrice}
            />
          </Tooltip>
        </Grid>
        <Grid item md={6}>
          {/* Onsite Visit Price */}
          <TextField
            size="small"
            fullWidth
            label="Onsite Visit Price"
            name="onsiteVisitPrice"
            type="number"
            value={formik.values.onsiteVisitPrice}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item md={6}>
          {/* Urgent Price */}
          <TextField
            fullWidth
            size="small"
            label="Urgent Charges"
            name="urgentCharge"
            type="number"
            value={formik.values.urgentCharge}
            onChange={formik.handleChange}
            error={formik.touched.urgentCharge && Boolean(formik.errors.urgentCharge)}
            helperText={formik.touched.urgentCharge && formik.errors.urgentCharge}
          />
        </Grid>
        <Grid item md={12} xs={12}>
          <AsyncAutocomplete
            placeholder="Search passport country"
            prefetch
            multiple
            value={formik.values.sizeStandard}
            onChange={v => formik.setFieldValue("sizeStandard", v)}
            fetchOptions={(text) => fetchOptions(apiUrl.masters.passportSize, text)}
            isOptionEqualToValue={(value, option) => value._id === option._id}
            getOptionLabel={o => `${o.country} (${o.lenInMM} * ${o.widthInMM})`}
            error={formik.touched.sizeStandard && !!formik.errors.sizeStandard}
            helperText={formik.touched.sizeStandard && formik.errors.sizeStandard}
          />
        </Grid>

        <Grid item md={12} xs={12}>
          <AsyncAutocomplete
            placeholder="Search Additional Service"
            prefetch
            multiple
            value={selectedAddService}
            onChange={v => setSelectedAddService(v)}
            fetchOptions={(text) => fetchOptions(apiUrl.masters.passportAdditionalService, text)}
            getOptionLabel={o => `${o.name}`}
            isOptionEqualToValue={(value, option) => value._id === option._id}
            error={formik.touched.additionalService && !!formik.errors.additionalService}
            helperText={formik.touched.additionalService && formik.errors.additionalService}
            sx={{ my: 2 }}
          />

          {selectedAddService?.length > 0 &&
            <Box className="table-responsive bg-white">
              <table
                className="table table-striped table-bordered admintable border-0"
                cellSpacing="0"
                cellPadding="0"
              >
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Price</th>
                  </tr>
                </thead>

                <tbody>
                  {
                    selectedAddService?.map((item, index) =>
                      <tr key={index}>
                        <td>{item?.name}</td>
                        <td>
                          <input style={{ width: '80px' }} value={item?.price} onChange={(e) => handlePrice(e, index)} />
                        </td>
                      </tr>
                    )
                  }
                </tbody>
              </table>
            </Box>
          }
        </Grid>
        <Grid item md={12}>
          <Box>
            <Typography variant="subtitle2">Remarks</Typography>
            <TextField
              fullWidth
              size="small"
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target.value.trim()) {
                  e.preventDefault();
                  formik.setFieldValue("remarks", [
                    ...formik.values.remarks,
                    e.target.value.trim(),
                  ]);
                  e.target.value = "";
                }
              }}
            />
            <Stack direction="row" flexWrap="wrap" gap={1} my={1}>
              {formik.values.remarks.map((remark, idx) => (
                <Chip
                  key={idx}
                  label={remark}
                  onDelete={() =>
                    formik.setFieldValue(
                      "remarks",
                      formik.values.remarks.filter((_, i) => i !== idx)
                    )
                  }
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Box>
        </Grid>
        <Grid item md={12}>
          <Typography variant="subtitle2">About Service</Typography>
          <TextField
            fullWidth
            size="small"
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value.trim()) {
                e.preventDefault();
                formik.setFieldValue("aboutService", [
                  ...formik.values.aboutService,
                  e.target.value.trim(),
                ]);
                e.target.value = "";
              }
            }}
          />
          <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
            {formik.values.aboutService.map((text, idx) => (
              <Chip
                key={idx}
                label={text}
                onDelete={() =>
                  formik.setFieldValue(
                    "aboutService",
                    formik.values.aboutService.filter((_, i) => i !== idx)
                  )
                }
                color="secondary"
                variant="outlined"
              />
            ))}
          </Stack>
        </Grid>
        <Grid item md={12}>
          <Typography variant="subtitle2">Service Process Steps</Typography>
          <TextField
            fullWidth
            size="small"
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value.trim()) {
                e.preventDefault();
                formik.setFieldValue("serviceProcessSteps", [
                  ...formik.values.serviceProcessSteps,
                  e.target.value.trim(),
                ]);
                e.target.value = "";
              }
            }}
          />
          <Stack direction="row" flexWrap="wrap" gap={1} my={1}>
            {formik.values.serviceProcessSteps.map((step, idx) => (
              <Chip
                key={idx}
                label={step}
                onDelete={() =>
                  formik.setFieldValue(
                    "serviceProcessSteps",
                    formik.values.serviceProcessSteps.filter((_, i) => i !== idx)
                  )
                }
                color="success"
                variant="outlined"
              />
            ))}
          </Stack>
        </Grid>
        <Grid item md={12}>
          {/* Save */}
          <Box display={'flex'} justifyContent={'center'}>
            <LoadingButton
              disabled={formik.isSubmitting || load}
              sx={{ width: 200 }} loading={load} type="submit" variant="contained">
              {defaultData?.flow === "ADD" ? 'Save Service' : 'Update Service'}
            </LoadingButton>
          </Box>
        </Grid>
      </Grid>

    </form>
  );
}
